import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Play, Pause, Music, Check } from "lucide-react";

// TikTok-style music/sound library backed by the Apple/iTunes catalog (30s previews).
export const MusicPicker = ({ open, onOpenChange, onSelect }) => {
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("trending");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);

  const search = useCallback(async ({ term = "", genre = "" }) => {
    setLoading(true);
    try {
      const { data } = await api.get("/music/search", { params: { q: term, genre } });
      setResults(data.results || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/music/categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
    search({ genre: "trending" });
    return () => { stopAudio(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingKey(null);
  };

  const togglePlay = (track) => {
    if (playingKey === track.key) { stopAudio(); return; }
    stopAudio();
    if (!track.preview_url) return;
    const a = new Audio(track.preview_url);
    a.volume = 1;
    a.play().catch(() => {});
    a.onended = () => setPlayingKey(null);
    audioRef.current = a;
    setPlayingKey(track.key);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setActiveCat("");
    search({ term: q });
  };

  const pickCat = (c) => {
    setActiveCat(c.key);
    setQ("");
    search({ genre: c.key });
  };

  const choose = (track) => {
    stopAudio();
    onSelect?.(track);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) stopAudio(); onOpenChange(v); }}>
      <SheetContent side="bottom" className="rounded-t-2xl h-[80vh] flex flex-col" data-testid="music-picker">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2"><Music className="h-5 w-5 text-primary" /> Bibliothèque musicale</SheetTitle>
        </SheetHeader>

        <form onSubmit={onSearchSubmit} className="relative mt-2">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un titre ou un artiste…"
            className="pl-9 rounded-xl"
            data-testid="music-search-input"
          />
        </form>

        <div className="flex gap-2 overflow-x-auto no-scrollbar py-3">
          {cats.map((c) => (
            <button
              key={c.key}
              onClick={() => pickCat(c)}
              data-testid={`music-cat-${c.key}`}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${activeCat === c.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto space-y-1.5 pb-4" data-testid="music-results">
          {loading ? (
            [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)
          ) : results.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Aucun résultat. Essayez un autre titre ou artiste.</p>
          ) : (
            results.map((track) => (
              <div key={track.key} className="flex items-center gap-3 rounded-xl p-2 hover:bg-secondary/60 transition-colors" data-testid={`music-item-${track.key}`}>
                <button
                  onClick={() => togglePlay(track)}
                  className="relative h-11 w-11 rounded-lg overflow-hidden bg-secondary grid place-items-center shrink-0"
                  aria-label="Écouter l'extrait"
                  data-testid={`music-play-${track.key}`}
                >
                  {track.artwork_url ? (
                    <img src={track.artwork_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                  ) : null}
                  <span className="relative z-10 h-6 w-6 rounded-full bg-black/55 grid place-items-center text-white">
                    {playingKey === track.key ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 fill-white" />}
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{track.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{track.artist}{track.genre ? ` · ${track.genre}` : ""}</p>
                </div>
                <Button size="sm" className="rounded-xl shrink-0" onClick={() => choose(track)} data-testid={`music-use-${track.key}`}>
                  <Check className="h-4 w-4 mr-1" /> Utiliser
                </Button>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MusicPicker;
